import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import {
  formatPhoneNumberForAPI,
  toLocalDisplay,
  validatePhoneNumberForCountry,
  ALL_COUNTRIES,
  TOP_COUNTRIES,
} from '../../utils/formatters';
import { useAffiliateTracking } from '../../hooks/useAffiliateTracking';
import { usePromoTracking } from '../../hooks/usePromoTracking';
import { useTrialOffer } from '@shared/ui/lib/useTrialOffer';
import { readCheckoutIntent } from '@shared/ui/checkout/checkout-intent';
import analyticsService from '../../services/analytics';
import googleAnalytics from '../../services/googleAnalytics';
import MetaPixelService from '../../services/metaPixel';
import { getAttribution } from '../../utils/attribution';
import { trialCopy } from '../../utils/trialCopy';

/* ------------------------------------------------------------------ */
/* Validation helpers                                                   */
/* ------------------------------------------------------------------ */

const validateEmail = (value: string): string => {
  if (!value.trim()) return 'Email address is required';
  if (!/\S+@\S+\.\S+/.test(value)) return 'Please enter a valid email address';
  return '';
};

const validateName = (value: string, label: string): string => {
  if (!value.trim()) return `${label} is required`;
  if (value.trim().length < 2) return `${label} must be at least 2 characters`;
  return '';
};

const validatePassword = (value: string): string => {
  if (!value) return 'Password is required';
  if (value.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(value)) return 'Must contain an uppercase letter';
  if (!/[a-z]/.test(value)) return 'Must contain a lowercase letter';
  if (!/[0-9]/.test(value)) return 'Must contain a number';
  if (!/[^A-Za-z0-9]/.test(value)) return 'Must contain a special character';
  return '';
};

const validateConfirmPassword = (value: string, password: string): string => {
  if (!value) return 'Please confirm your password';
  if (value !== password) return 'Passwords do not match';
  return '';
};

/* ------------------------------------------------------------------ */
/* Password strength helpers                                            */
/* ------------------------------------------------------------------ */

const getPasswordStrength = (pw: string): number => {
  let score = 0;
  if (pw.length >= 8) score += 20;
  if (/[A-Z]/.test(pw)) score += 20;
  if (/[a-z]/.test(pw)) score += 20;
  if (/[0-9]/.test(pw)) score += 20;
  if (/[^A-Za-z0-9]/.test(pw)) score += 20;
  return score;
};

const getStrengthInfo = (score: number) => {
  if (score <= 20) return { color: '#ef4444', label: 'Very weak',  textClass: 'text-red-400' };
  if (score <= 40) return { color: '#f97316', label: 'Weak',       textClass: 'text-orange-400' };
  if (score <= 60) return { color: '#eab308', label: 'Fair',       textClass: 'text-yellow-400' };
  if (score <= 80) return { color: '#3b82f6', label: 'Good',       textClass: 'text-blue-400' };
  return            { color: '#22c55e', label: 'Strong',     textClass: 'text-green-400' };
};

/* ------------------------------------------------------------------ */
/* Styling helpers                                                      */
/* ------------------------------------------------------------------ */

const inputClass = (hasError: boolean): string =>
  `w-full bg-[#1f2937] border rounded-lg px-3.5 py-3 text-white text-[15px] placeholder-[#4b5563] outline-none transition-all duration-150 focus:ring-2 ${
    hasError
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
      : 'border-[#374151] focus:border-racing-green focus:ring-racing-green/20'
  }`;

/* ------------------------------------------------------------------ */
/* EyeIcon                                                              */
/* ------------------------------------------------------------------ */

const EyeIcon: React.FC<{ visible: boolean }> = ({ visible }) =>
  visible ? (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L12 12m-3.122-3.122a3 3 0 013.122-3.122m0 0L15 9m-6 6l6 6" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

/** The funnel's name for each input, as the registration events have always used. */
const FIELD_NAMES: Record<string, string> = {
  'tsf-firstName': 'firstName',
  'tsf-lastName':  'lastName',
  'tsf-email':     'email',
  'tsf-phone':     'phoneNumber',
  'tsf-password':  'password',
  'tsf-confirm':   'confirmPassword',
};

/** Analytics never gets in the way of signing up. */
const safely = (fn: () => void) => {
  try {
    fn();
  } catch (e) {
    console.error('Analytics error:', e);
  }
};

/**
 * The free-trial sign-up form, on the landing page and the register page.
 * `source` says which, in the registration funnel (started, each field,
 * submitted, success with the GA sign_up conversion, failure, abandoned).
 */
const TrialSignupForm: React.FC<{ source?: 'home' | 'register' }> = ({ source = 'home' }) => {
  const startedAt = useRef(Date.now());
  const started = useRef(false);
  const completed = useRef(new Set<string>());
  const finished = useRef(false); // signed up, or already counted as abandoned
  const lastError = useRef('');

  // Started on the first keystroke, then each field the first time it has a
  // value. Field names only - what people type stays out of analytics.
  const onFormInput = (e: React.FormEvent<HTMLFormElement>) => {
    const timeOnPage = Date.now() - startedAt.current;
    if (!started.current) {
      started.current = true;
      safely(() => analyticsService.trackRegistrationFormStarted({ source, timeOnPage }));
    }
    const input = e.target as HTMLInputElement;
    const field = FIELD_NAMES[input.id];
    if (!field || !input.value.trim() || completed.current.has(field)) return;
    completed.current.add(field);
    safely(() =>
      analyticsService.trackRegistrationFormFieldCompleted(field, {
        source,
        timeOnPage,
        totalFieldsCompleted: completed.current.size,
      }),
    );
  };

  // Abandoned: started but never signed up, by leaving the page in the app or closing the tab.
  useEffect(() => {
    const abandon = () => {
      if (!started.current || finished.current) return;
      finished.current = true;
      safely(() =>
        analyticsService.trackRegistrationAbandoned({
          source,
          timeOnPage: Date.now() - startedAt.current,
          fieldsCompleted: Array.from(completed.current),
          totalFieldsCompleted: completed.current.size,
          lastError: lastError.current || null,
        }),
      );
    };
    window.addEventListener('pagehide', abandon);
    return () => {
      window.removeEventListener('pagehide', abandon);
      abandon();
    };
  }, [source]);

  const [firstName, setFirstName]             = useState('');
  const [lastName, setLastName]               = useState('');
  const [email, setEmail]                     = useState('');
  const [phoneNumber, setPhoneNumber]         = useState('');
  const [dialCode, setDialCode]               = useState('+61');
  const [countryCode, setCountryCode]         = useState('AU');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword]       = useState(false);
  const [showConfirm, setShowConfirm]         = useState(false);
  const [error, setError]                     = useState('');
  const [fieldErrors, setFieldErrors]         = useState({
    firstName:       '',
    lastName:        '',
    email:           '',
    phoneNumber:     '',
    password:        '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const submissionInProgress                  = useRef(false);

  const { register }                      = useAuth();
  const navigate                          = useNavigate();
  const skipVerification                  = useFeatureFlag('skipVerification');
  const { getAffiliateForRegistration }   = useAffiliateTracking();
  const { getPromoSourceForRegistration } = usePromoTracking();

  // What signing up today gets, from the brand's trial package (none while it is switched off)
  const { trialDays } = useTrialOffer();
  // Sent here by a direct checkout link (/buy/... passes itself as `from`) with the checkout
  // still saved: after sign-up they go straight on to pay. A checkout saved on an earlier
  // visit alone doesn't count, or a plain visit to the sign-up page would lose its trial wording.
  const location = useLocation();
  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '';
  const [checkoutPending] = useState(() => fromPath.startsWith('/buy/') && readCheckoutIntent() !== null);
  const copy = checkoutPending
    ? { ...trialCopy(null), subtitle: "Create your account, then you'll go straight on to secure checkout" }
    : trialCopy(trialDays);

  /* ---- Country change ---- */
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setCountryCode(code);
    const country = ALL_COUNTRIES.find(c => c.code === code);
    if (country) setDialCode(country.dialCode);
  };

  /* ---- Blur validation ---- */
  const handleBlur = (field: keyof typeof fieldErrors) => {
    switch (field) {
      case 'firstName':
        setFieldErrors(p => ({ ...p, firstName: validateName(firstName, 'First name') })); break;
      case 'lastName':
        setFieldErrors(p => ({ ...p, lastName: validateName(lastName, 'Last name') })); break;
      case 'email':
        setFieldErrors(p => ({ ...p, email: validateEmail(email) })); break;
      case 'phoneNumber': {
        // Normalise what was typed (e.g. "+61404211211" -> "0404211211") and check it
        const normalisedPhone = toLocalDisplay(phoneNumber, dialCode);
        if (normalisedPhone !== phoneNumber) setPhoneNumber(normalisedPhone);
        setFieldErrors(p => ({
          ...p,
          phoneNumber: normalisedPhone.trim()
            ? validatePhoneNumberForCountry(normalisedPhone, dialCode)
            : 'Mobile number is required',
        }));
        break;
      }
      case 'password':
        setFieldErrors(p => ({ ...p, password: validatePassword(password) }));
        if (confirmPassword) setFieldErrors(p => ({ ...p, confirmPassword: validateConfirmPassword(confirmPassword, password) }));
        break;
      case 'confirmPassword':
        setFieldErrors(p => ({ ...p, confirmPassword: validateConfirmPassword(confirmPassword, password) })); break;
    }
  };

  /* ---- Submit ---- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionInProgress.current || isSubmitting) return;

    const errs = {
      firstName:       validateName(firstName, 'First name'),
      lastName:        validateName(lastName, 'Last name'),
      email:           validateEmail(email),
      phoneNumber:     phoneNumber.trim() ? '' : 'Mobile number is required',
      password:        validatePassword(password),
      confirmPassword: validateConfirmPassword(confirmPassword, password),
    };
    setFieldErrors(errs);
    safely(() =>
      analyticsService.trackRegistrationFormSubmitted({ source, timeOnPage: Date.now() - startedAt.current, fieldsCompleted: Array.from(completed.current) }),
    );

    if (Object.values(errs).some(Boolean)) {
      setError('Please correct the errors below before continuing.');
      return;
    }

    const phoneError = validatePhoneNumberForCountry(phoneNumber, dialCode);
    if (phoneError) {
      setFieldErrors(p => ({ ...p, phoneNumber: phoneError }));
      return;
    }

    const formattedPhone = formatPhoneNumberForAPI(phoneNumber, dialCode);

    try {
      setError('');
      setIsSubmitting(true);
      submissionInProgress.current = true;

      const affiliateCode = getAffiliateForRegistration();
      const promoSource   = getPromoSourceForRegistration();
      await register({
        phoneNumber:      formattedPhone,
        email,
        firstName,
        lastName,
        password,
        marketingConsent: true,
        affiliateCode,
        promoSource,
      });

      finished.current = true;
      safely(() => {
        googleAnalytics.trackSignUp();
        MetaPixelService.trackCompleteRegistration();
        analyticsService.trackRegistrationSuccess({
          source,
          timeOnPage: Date.now() - startedAt.current,
          fieldsCompleted: Array.from(completed.current),
          skipVerification,
          affiliateCode,
          promoSource,
          attribution: getAttribution(),
        });
      });

      if (skipVerification) {
        navigate('/login', { state: { message: 'Registration successful. You can now log in.' } });
      } else {
        navigate('/verify-phone', { state: { phoneNumber } });
      }
    } catch (err: any) {
      lastError.current = err.message || 'unknown';
      safely(() =>
        analyticsService.trackEvent('registration_failure', {
          pageType: 'registration',
          metadata: { source, errorMessage: err.message, timeOnPage: Date.now() - startedAt.current },
        }),
      );
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      submissionInProgress.current = false;
    }
  };

  const topCountryCodes = new Set(TOP_COUNTRIES.map(c => c.code));
  const otherCountries  = ALL_COUNTRIES.filter(c => !topCountryCodes.has(c.code));
  const strengthScore   = getPasswordStrength(password);
  const strengthInfo    = getStrengthInfo(strengthScore);

  const labelClass = 'text-[13px] font-semibold text-gray-300 tracking-wide';
  const fieldWrap  = 'flex flex-col gap-1.5';
  const errClass   = 'text-xs text-red-400 m-0';

  return (
    <div
      className="bg-[#111827] border border-[#1f2937] rounded-2xl p-8 sm:p-10"
      role="region"
      aria-label={copy.heading}
    >
      <h2 className="text-2xl font-extrabold text-white m-0 mb-1.5">{copy.heading}</h2>
      <p className="text-sm text-gray-400 m-0 mb-7">{copy.subtitle}</p>

      {error && (
        <div
          className="bg-[#1f1315] border border-red-900 rounded-lg px-4 py-3 text-red-300 text-sm mb-5"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit} onInput={onFormInput} noValidate>
        {/* Name row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className={fieldWrap}>
            <label className={labelClass} htmlFor="tsf-firstName">First Name</label>
            <input
              id="tsf-firstName"
              type="text"
              autoComplete="given-name"
              placeholder="John"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              onBlur={() => handleBlur('firstName')}
              className={inputClass(!!fieldErrors.firstName)}
              aria-invalid={!!fieldErrors.firstName}
            />
            {fieldErrors.firstName && <p className={errClass} role="alert">{fieldErrors.firstName}</p>}
          </div>

          <div className={fieldWrap}>
            <label className={labelClass} htmlFor="tsf-lastName">Last Name</label>
            <input
              id="tsf-lastName"
              type="text"
              autoComplete="family-name"
              placeholder="Smith"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              onBlur={() => handleBlur('lastName')}
              className={inputClass(!!fieldErrors.lastName)}
              aria-invalid={!!fieldErrors.lastName}
            />
            {fieldErrors.lastName && <p className={errClass} role="alert">{fieldErrors.lastName}</p>}
          </div>
        </div>

        {/* Email */}
        <div className={fieldWrap}>
          <label className={labelClass} htmlFor="tsf-email">Email Address</label>
          <input
            id="tsf-email"
            type="email"
            autoComplete="email"
            placeholder="your.name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={() => handleBlur('email')}
            className={inputClass(!!fieldErrors.email)}
            aria-invalid={!!fieldErrors.email}
          />
          {fieldErrors.email && <p className={errClass} role="alert">{fieldErrors.email}</p>}
        </div>

        {/* Phone with country selector */}
        <div className={fieldWrap}>
          <label className={labelClass} htmlFor="tsf-phone">Mobile Number</label>
          <div className={`flex border rounded-lg overflow-hidden transition-all duration-150 focus-within:ring-2 ${
            fieldErrors.phoneNumber
              ? 'border-red-500 focus-within:ring-red-500/20'
              : 'border-[#374151] focus-within:border-racing-green focus-within:ring-racing-green/20'
          }`}>
            <select
              value={countryCode}
              onChange={handleCountryChange}
              className="bg-[#1f2937] border-r border-[#374151] text-gray-300 text-sm pl-2 pr-1 py-3 outline-none cursor-pointer appearance-none flex-shrink-0"
              aria-label="Country dial code"
            >
              <optgroup>
                {TOP_COUNTRIES.map(c => (
                  <option key={`top-${c.code}`} value={c.code}>
                    {c.flag} {c.code} {c.dialCode}
                  </option>
                ))}
              </optgroup>
              <optgroup>
                {otherCountries.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} {c.dialCode}
                  </option>
                ))}
              </optgroup>
            </select>
            <input
              id="tsf-phone"
              type="tel"
              inputMode="numeric"
              pattern="[0-9\s]*"
              autoComplete="tel"
              placeholder={ALL_COUNTRIES.find(c => c.code === countryCode)?.placeholder ?? '04XX XXX XXX'}
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              onBlur={() => handleBlur('phoneNumber')}
              className="flex-1 min-w-0 bg-[#1f2937] border-none px-3.5 py-3 text-white text-[15px] placeholder-[#4b5563] outline-none"
              aria-invalid={!!fieldErrors.phoneNumber}
            />
          </div>
          {fieldErrors.phoneNumber && <p className={errClass} role="alert">{fieldErrors.phoneNumber}</p>}

          <p className="text-[11px] text-gray-500 mt-1">
            {dialCode === '+1'
              ? 'Your phone number is used as your username to log in.'
              : 'Used for secure authentication only. We will not share your number with third parties.'}
          </p>
        </div>

        {/* Password */}
        <div className={fieldWrap}>
          <label className={labelClass} htmlFor="tsf-password">Password</label>
          <div className="relative">
            <input
              id="tsf-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a strong password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              className={inputClass(!!fieldErrors.password) + ' pr-11'}
              aria-invalid={!!fieldErrors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={showPassword} />
            </button>
          </div>
          {fieldErrors.password ? (
            <p className={errClass} role="alert">{fieldErrors.password}</p>
          ) : password ? (
            <>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-500">Password strength</span>
                <span className={`text-xs font-semibold ${strengthInfo.textClass}`}>{strengthInfo.label}</span>
              </div>
              <div className="w-full bg-[#374151] rounded-full h-1 mt-1">
                <div
                  className="h-1 rounded-full transition-all duration-300"
                  style={{ width: `${strengthScore}%`, backgroundColor: strengthInfo.color }}
                />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {[
                  { label: '8+ chars', met: password.length >= 8 },
                  { label: 'ABC',      met: /[A-Z]/.test(password) },
                  { label: 'abc',      met: /[a-z]/.test(password) },
                  { label: '123',      met: /[0-9]/.test(password) },
                  { label: '!@#',      met: /[^A-Za-z0-9]/.test(password) },
                ].map(chip => (
                  <span
                    key={chip.label}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${
                      chip.met
                        ? 'bg-green-900/20 text-green-400 border-green-700/40'
                        : 'bg-[#1f2937] text-gray-500 border-[#374151]'
                    }`}
                  >
                    {chip.label}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/* Confirm Password */}
        <div className={fieldWrap}>
          <label className={labelClass} htmlFor="tsf-confirm">Confirm Password</label>
          <div className="relative">
            <input
              id="tsf-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onBlur={() => handleBlur('confirmPassword')}
              className={inputClass(!!fieldErrors.confirmPassword) + ' pr-11'}
              aria-invalid={!!fieldErrors.confirmPassword}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              <EyeIcon visible={showConfirm} />
            </button>
          </div>
          {fieldErrors.confirmPassword && (
            <p className={errClass} role="alert">{fieldErrors.confirmPassword}</p>
          )}
        </div>

        {/* Email consent notice */}
        <div className="flex items-start gap-2 bg-blue-900/20 border border-blue-700/40 rounded-lg px-3 py-2.5 text-[11px] text-blue-300 leading-relaxed" role="note">
          <svg className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            By creating an account you agree to receive racing tips and occasional offers from us by email. You can unsubscribe at any time via the link in any email, or from your profile settings. See our <a href="/privacy" className="underline text-blue-200">Privacy Policy</a>. You must be 18 or over to sign up.
          </span>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
          className="mt-1 w-full bg-racing-green text-white font-extrabold text-sm tracking-[0.08em] uppercase rounded-lg py-3.5 px-6 transition-all duration-150 hover:bg-racing-green-dark hover:-translate-y-px active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer border-0"
        >
          {isSubmitting ? (
            <>
              <span
                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0"
                aria-hidden="true"
              />
              Creating account…
            </>
          ) : (
            copy.button
          )}
        </button>
      </form>

      <p className="mt-4 text-center text-[13px] text-gray-500">
        Already a member?{' '}
        <Link to="/login" className="text-racing-green font-medium hover:underline">
          Sign in
        </Link>
      </p>

      <div
        className="mt-4 flex items-center justify-center gap-1.5 text-[12px] text-[#4b5563] flex-wrap"
        aria-label="Trust indicators"
      >
        <span>Secure</span>
        <span aria-hidden="true" className="text-[#374151]">·</span>
        <span>tippingiq.com.au</span>
        <span aria-hidden="true" className="text-[#374151]">·</span>
        <span>AU-hosted data</span>
      </div>
    </div>
  );
};

export default TrialSignupForm;
